import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  MapPin, Phone, Star, Camera, CheckCircle, Clock, Award,
  MessageCircle, ChevronRight, Shield, ArrowRight, X, Calendar,
  Search, Eye, Users
} from "lucide-react";
import { ContactSection, SectionDivider } from "@/Layout";
import { WA_LINK, CALENDLY } from "@/Layout";
import { WhatsAppBookingForm } from "@/components/WhatsAppBookingForm";
import { CrossSellingGift, CrossSellingProducts } from "@/components/CrossSelling";

// Star rating (Amazon-style)
const StarRating = ({ rating, count }) => (
  <div className="flex items-center gap-1">
    {[1,2,3,4,5].map(s => (
      <Star key={s} className={`w-4 h-4 ${s <= Math.floor(rating) ? 'text-[#FFA41C] fill-[#FFA41C]' : s <= rating ? 'text-[#FFA41C] fill-[#FFA41C] opacity-50' : 'text-gray-300'}`} />
    ))}
    <span className="text-sm text-blue-600 ml-1">({count})</span>
  </div>
);

// Start Pricing Card
const StartPricingCard = () => {
  const [showWarning, setShowWarning] = useState(false);
  const [step, setStep] = useState(0);

  const handleClick = () => {
    if (step === 0) { setShowWarning(true); setStep(1); }
    else if (step === 1) { setStep(2); }
    else { window.open('https://www.paypal.me/Riccarossi/99', '_blank'); }
  };

  return (
    <section className="py-16 bg-[#0c1220]">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back link */}
        <Link to="/" className="inline-flex items-center gap-2 text-gray-500 hover:text-[#ff6b35] mb-8 transition-colors" data-testid="back-home">
          <ChevronRight className="w-4 h-4 rotate-180" /> Torna alle 4 porte
        </Link>

        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <span className="w-4 h-4 rounded-full bg-[#EA4335]"></span>
            <Badge className="bg-red-100 text-[#EA4335] border-red-300">Pacchetto Start</Badge>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            Start — <span className="text-[#EA4335]">#DignitàDigitale</span>
          </h1>
          <p className="text-gray-500">Servizi essenziali per la TUA scheda Google</p>
        </div>

        <div className="bg-white/5 rounded-2xl border-2 border-red-500/20 p-6 md:p-8">
          <StarRating rating={4} count={12} />
          <div className="mt-4 mb-2">
            <div className="flex items-baseline">
              <span className="text-sm text-gray-400 mr-1">€</span>
              <span className="text-5xl font-bold text-white tracking-tight">99</span>
            </div>
            <p className="text-xs text-gray-500">una tantum · oppure €8,25/mese x12</p>
          </div>

          <div className="bg-red-900/20 border border-red-500/20 rounded-lg px-3 py-2 mb-6">
            <p className="text-xs text-[#EA4335] font-medium">
              <Clock className="w-3 h-3 inline mr-1" />
              Valido entro <strong>7 giorni</strong> dal primo contatto
            </p>
          </div>

          <ul className="space-y-2 mb-6">
            {["Verifica e claim della scheda", "Correzione info base (NAP)", "Inserimento orari e categoria", "1 foto profilo ottimizzata", "Report iniziale", "3 foto sferiche/immersive in REGALO"].map((f, i) => (
              <li key={i} className="flex items-start gap-2 border border-white/20 rounded-lg px-3 py-2 bg-white/[0.03]">
                <CheckCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${f.includes('REGALO') ? 'text-[#ff6b35]' : 'text-[#EA4335]'}`} />
                <span className={f.includes('REGALO') ? 'text-[#ff6b35] font-bold' : 'text-gray-300'}>{f}</span>
              </li>
            ))}
            {["Recupero sospensione", "Risposta recensioni", "Consulenza strategica", "Virtual Tour 360"].map((f, i) => (
              <li key={`x-${i}`} className="flex items-start gap-2 opacity-40">
                <X className="w-5 h-5 flex-shrink-0 mt-0.5 text-gray-400" />
                <span className="text-gray-400 line-through">{f}</span>
              </li>
            ))}
          </ul>

          <button
            className="w-full bg-[#FFD814] hover:bg-[#F7CA00] text-gray-900 font-bold py-4 rounded-full border border-[#FCD200] shadow-md transition-all duration-200 active:scale-95 text-lg relative overflow-hidden group"
            onClick={handleClick}
            data-testid="start-pkg-btn"
          >
            <span className="relative z-10 inline-flex items-center justify-center gap-2">
              <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none"><path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.771.771 0 0 1 .761-.652h6.199c2.063 0 3.543.585 4.268 1.694.335.516.538 1.087.617 1.742.083.677.032 1.478-.157 2.398l-.013.063v.564l.44.249c.372.21.67.455.893.735.37.464.607 1.042.7 1.715.096.693.037 1.498-.173 2.393-.241 1.028-.635 1.922-1.171 2.655a5.292 5.292 0 0 1-1.539 1.546c-.593.39-1.298.672-2.097.84-.769.161-1.64.245-2.582.245h-.614a1.793 1.793 0 0 0-1.77 1.513l-.046.244-.77 4.88-.036.174a.164.164 0 0 1-.047.1.13.13 0 0 1-.078.028z" fill="#253B80"/><path d="M18.36 7.667a7.37 7.37 0 0 0-.078.449c-1 5.155-4.426 6.933-8.801 6.933h-2.228a1.08 1.08 0 0 0-1.07.918L5.09 22.83l-.323 2.054a.57.57 0 0 0 .563.66h3.952a.95.95 0 0 0 .94-.803l.038-.201.744-4.728.048-.26a.951.951 0 0 1 .94-.804h.591c3.83 0 6.828-1.556 7.704-6.063.366-1.882.176-3.453-.793-4.558a3.793 3.793 0 0 0-1.082-.835z" fill="#179BD7"/></svg>
              <span className="cta-wave-text">{step === 0 ? 'Paga con PayPal — €99' : step === 1 ? 'Sei davvero sicuro?' : 'Conferma Start'}</span>
            </span>
            <span className="cta-talpa-shine"></span>
          </button>
          <div className="flex items-center justify-center gap-2 mt-2">
            <svg className="h-4 w-auto" viewBox="0 0 101 32" fill="#003087"><path d="M12.2 4.6h-7c-.5 0-.9.3-1 .8L1.5 24c-.1.4.2.7.6.7h3.4c.5 0 .9-.3 1-.8l.8-5.1c.1-.5.5-.8 1-.8h2.3c4.7 0 7.5-2.3 8.2-6.8.3-2 0-3.5-.9-4.6C16.8 5.3 14.8 4.6 12.2 4.6z"/><path d="M39.6 4.6h-7c-.5 0-.9.3-1 .8l-2.7 18.6c-.1.4.2.7.6.7h3.6c.3 0 .6-.2.7-.6l.8-5.3c.1-.5.5-.8 1-.8h2.3c4.7 0 7.5-2.3 8.2-6.8.3-2 0-3.5-.9-4.6C44.2 5.3 42.2 4.6 39.6 4.6z"/></svg>
            <span className="text-xs text-gray-500">oppure <strong className="text-[#003087]">€33 x 3 rate</strong> · TAEG 0%</span>
          </div>

          {showWarning && (
            <div className="mt-4 bg-amber-50 border-2 border-amber-300 rounded-xl p-4 animate-fadeIn">
              <p className="text-amber-800 font-bold text-sm mb-2">Aspetta... sei sicuro?</p>
              <p className="text-amber-700 text-xs mb-3">
                Con il <strong>Plus</strong> hai assistenza completa:
                sospensione, recensioni, consulenza strategica a soli <strong>€299</strong> (€99,66 x 3 con PayPal!)
              </p>
              {step >= 2 && (
                <div className="bg-red-900/20 border border-red-500/20 rounded-lg p-2 mb-3 animate-fadeIn">
                  <p className="text-red-700 text-xs font-bold">
                    Oppure scegli <strong>Virtual Tour 360</strong> a €499: Virtual Tour + Start INCLUSO!
                  </p>
                </div>
              )}
              <Link to="/plus">
                <button className="w-full bg-[#FFD814] hover:bg-[#F7CA00] text-gray-900 font-bold py-2 rounded-full border border-[#FCD200] text-sm">
                  Passa al Plus
                </button>
              </Link>
            </div>
          )}
        </div>

        {/* Comparison note */}
        <div className="mt-6 text-center">
          <Link to="/rooftop" className="text-sm text-gray-500 hover:text-amber-600 transition-colors">
            Vedi anche il pacchetto <strong className="text-amber-600">Rooftop €599</strong> — TUTTO incluso + assistenza premium →
          </Link>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const [expanded, setExpanded] = useState(null);
  const services = [
    { id: 1, title: "Scheda Sospesa", shortDesc: "Ricorso e ripristino in 72h", fullDesc: "Presento il reclamo ufficiale a Google con tutta la documentazione necessaria.", image: "https://images.unsplash.com/photo-1633613286991-611fe299c4be?w=400", price: "Da €99", tag: "URGENTE" },
    { id: 2, title: "Cambio Sede/GPS", shortDesc: "Sposto il pin nella posizione corretta", fullDesc: "Se il puntatore sulla mappa è nel posto sbagliato, i clienti non ti trovano. Correggo la posizione GPS.", image: "https://images.unsplash.com/photo-1552332698-d36c3c77dfbd?w=400", price: "Da €79", tag: "POPULAR" },
    { id: 3, title: "Cambio Info NAP", shortDesc: "Nome, Indirizzo, Telefono", fullDesc: "Modifico le informazioni sensibili della tua scheda: nome attività, indirizzo completo, numero di telefono.", image: "https://images.unsplash.com/photo-1727787571272-2d894258a1d6?w=400", price: "Da €59", tag: null },
    { id: 4, title: "Link e Social", shortDesc: "Sito, WhatsApp, Instagram collegati", fullDesc: "Inserisco tutti i link ai tuoi canali: sito web, WhatsApp Business, Facebook, Instagram.", image: "https://images.unsplash.com/photo-1675119711588-ecd395253cec?w=400", price: "Da €49", tag: null },
    { id: 5, title: "Foto 360° Aggiornate", shortDesc: "Sostituisco foto vecchie o brutte", fullDesc: "Realizzo nuove foto professionali a 360° che valorizzano la tua attività.", image: "https://images.unsplash.com/photo-1661082565547-91c046200cc2?w=400", price: "Da €199", tag: "FOTO" },
    { id: 6, title: "Formazione 2 Ore", shortDesc: "Impara a valorizzare la tua scheda", fullDesc: "Ti insegno a valorizzare in autonomia la tua scheda Google: rispondere alle recensioni, aggiornare info, pubblicare post.", image: "https://images.unsplash.com/photo-1755548413928-4aaeba7c740e?w=400", price: "€99", tag: "FORMAZIONE" },
    { id: 7, title: "Valutazione Profilo", shortDesc: "REGALO di benvenuto · Da 0 a 5 stelle", fullDesc: "Analizzo personalmente la tua scheda Google e ti do una valutazione professionale.", image: "https://images.unsplash.com/photo-1765020553734-2c050ddb9494?w=400", price: "REGALO", tag: "WELCOME" },
  ];
  const tagColors = { "URGENTE": "bg-red-500", "POPULAR": "bg-green-500", "FOTO": "bg-blue-500", "FORMAZIONE": "bg-yellow-500 text-gray-900", "WELCOME": "bg-gradient-to-r from-[#ff6b35] to-pink-500" };

  return (
    <section id="servizi" className="py-16 bg-[#0a0e1a]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">Cosa Posso Fare Per Te</h2>
          <p className="text-gray-500">Scegli il servizio · Clicca per i dettagli</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {services.map((service) => (
            <div key={service.id} className="relative group cursor-pointer" onClick={() => setExpanded(expanded === service.id ? null : service.id)}>
              <div className={`bg-white/5 rounded-2xl overflow-hidden border-2 transition-all duration-300 ${expanded === service.id ? 'border-[#ff6b35] shadow-xl' : 'border-white/10 hover:border-[#ff6b35]/50'}`}>
                <div className="relative h-32 md:h-40 overflow-hidden">
                  <img src={service.image} alt={service.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  {service.tag && <span className={`absolute top-2 right-2 ${tagColors[service.tag]} text-white text-xs font-bold px-2 py-1 rounded-full`}>{service.tag}</span>}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                    <span className="text-white font-bold text-lg">{service.price}</span>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-white mb-1">{service.title}</h3>
                  <p className="text-gray-500 text-sm">{service.shortDesc}</p>
                  <button className="mt-2 text-[#ff6b35] text-sm font-medium flex items-center gap-1">
                    {expanded === service.id ? 'Chiudi' : 'Continua a leggere'}
                    <ChevronRight className={`w-4 h-4 transition-transform ${expanded === service.id ? 'rotate-90' : ''}`} />
                  </button>
                  {expanded === service.id && (
                    <div className="mt-3 pt-3 border-t border-white/10 animate-fadeIn">
                      <p className="text-gray-400 text-sm mb-3">{service.fullDesc}</p>
                      <Button size="sm" className="w-full bg-[#ff6b35] hover:bg-[#e55a2b] rounded-full" onClick={(e) => { e.stopPropagation(); window.open(`${WA_LINK}?text=Ciao! Sono interessato al servizio: ${service.title}`, '_blank'); }}>
                        <MessageCircle className="w-4 h-4 mr-1" /> Richiedi Info
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Profile Rating Section
const ProfileRatingSection = () => {
  const [profileLink, setProfileLink] = useState('');
  const [sent, setSent] = useState(false);
  const handlePaste = async () => { try { const text = await navigator.clipboard.readText(); setProfileLink(text); } catch {} };
  const handleSubmit = () => {
    if (!profileLink) return;
    const message = `Ciao Riccardo! Vorrei ricevere la valutazione del mio profilo Google.\n\nLink scheda: ${profileLink}\n\nGrazie!`;
    window.open(`${WA_LINK}?text=${encodeURIComponent(message)}`, '_blank');
    setSent(true);
  };

  return (
    <section className="py-12 bg-gradient-to-br from-gray-900 to-gray-800">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <Badge className="bg-[#ff6b35] text-white border-none mb-4 text-sm">REGALO di Benvenuto</Badge>
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-2">
            Scopri subito <span className="text-[#ff6b35]">QUANTO</span> è attrattiva<br/>la tua scheda su Google
          </h2>
          <p className="text-gray-400"><strong className="text-white">Un essere umano, certificato Google.</strong> Riccardo Rossi, il TUO consulente.</p>
        </div>
        <div className="bg-white/5 rounded-3xl p-6 md:p-8 border border-white/10">
          {!sent ? (
            <>
              <div className="mb-6">
                <label className="block text-gray-400 text-sm mb-2 font-medium">Incolla qui il link della tua scheda Google:</label>
                <div className="relative">
                  <input type="url" placeholder="https://share.google/..." value={profileLink} onChange={(e) => setProfileLink(e.target.value)}
                    className="w-full px-6 py-5 text-lg rounded-2xl border-2 border-white/10 focus:border-[#ff6b35] focus:ring-4 focus:ring-[#ff6b35]/20 outline-none transition-all pr-24" />
                  <button onClick={handlePaste} className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-gray-300 px-4 py-2 rounded-xl font-medium transition-colors">Incolla</button>
                </div>
              </div>
              <Button className="w-full bg-[#ff6b35] hover:bg-[#e55a2b] rounded-2xl py-6 text-xl font-bold" onClick={handleSubmit} disabled={!profileLink}>
                Invia e ricevi la valutazione
              </Button>
              <div className="mt-8 pt-8 border-t border-white/10">
                <p className="text-center text-gray-500 text-sm mb-4 font-medium">Come ottenere il link? È facilissimo:</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white/5 rounded-2xl p-4 relative">
                    <span className="absolute -top-3 -left-3 w-8 h-8 bg-[#ff6b35] text-white rounded-full flex items-center justify-center font-bold text-sm">1</span>
                    <img src="https://customer-assets.emergentagent.com/job_sos-activity-cards/artifacts/yhddab21_chrome_WvazoptoFf.png" alt="Trova la tua scheda" className="w-full rounded-xl mb-3" />
                    <p className="text-sm text-gray-400">Cerca la tua attività su Google e clicca <strong className="text-blue-400">"Condividi"</strong></p>
                  </div>
                  <div className="bg-white/5 rounded-2xl p-4 relative">
                    <span className="absolute -top-3 -left-3 w-8 h-8 bg-[#ff6b35] text-white rounded-full flex items-center justify-center font-bold text-sm">2</span>
                    <img src="https://customer-assets.emergentagent.com/job_sos-activity-cards/artifacts/ua6twkwr_chrome_XwHl40VwYP.png" alt="Copia il link" className="w-full rounded-xl mb-3" />
                    <p className="text-sm text-gray-400">Copia il <strong className="text-blue-400">link</strong> e incollalo qui sopra</p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"><CheckCircle className="w-10 h-10 text-green-500" /></div>
              <h3 className="text-2xl font-bold text-white mb-2">Perfetto!</h3>
              <p className="text-gray-400">Completa l'invio su WhatsApp e riceverai la valutazione via email.</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  const botAvatar = "https://static.prod-images.emergentagent.com/jobs/ed38cac5-608f-47c3-99f1-51c18b422996/images/754131ec4b2c15ff2bd3f4c7a531d54ec8c7766fac5ddf14e5dadf6f976609b3.png";
  const [openIndex, setOpenIndex] = useState(null);
  const faqs = [
    { question: "Quanto tempo ci vuole per ripristinare una scheda sospesa?", answer: "Nella maggior parte dei casi 48-72 ore. Per casi complessi fino a 2 settimane." },
    { question: "Quanto costa una consulenza?", answer: "La prima valutazione è il tuo regalo di benvenuto. I pacchetti: Start €99, Plus €299, Virtual Tour €499 (Start incluso!), Rooftop €599. Tutti con PayPal Paga in 3 rate TAEG 0%." },
    { question: "Lavori solo a Siena?", answer: "Principalmente Siena e Valdelsa, ma lavoro in tutta Italia da remoto." },
    { question: "Posso valorizzare la scheda da solo dopo?", answer: "Si'! Offro formazione di 2 ore a €99 (entro 7 giorni dal primo contatto) per renderti autonomo." },
    { question: "Come ti contatto?", answer: "WhatsApp al (+39) 327 383 9782 o prenota una chiamata su Calendly." },
  ];

  return (
    <section id="faq" className="py-16 bg-[#0c1220]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img src={botAvatar} alt="schedabusiness Assistenza" className="w-14 h-14 rounded-full shadow-lg" />
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-2 flex items-center justify-center gap-2">
            <span className="text-gray-400 font-mono">&lt;?</span>
            <span className="text-[#ff6b35] font-black">_schedabusiness_</span>
            <span className="text-gray-400 font-mono">?&gt;</span>
          </h2>
          <p className="text-gray-500 text-sm">Domande Frequenti</p>
        </div>
        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <div key={index} className="bg-white/5 rounded-xl border border-white/10 hover:border-[#ff6b35]/30 transition-all cursor-pointer overflow-hidden"
              onClick={() => setOpenIndex(openIndex === index ? null : index)} data-testid={`faq-${index}`}>
              <div className="p-4 flex items-center gap-3">
                <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-[#ff6b35] font-mono text-xs font-bold">?</span>
                </div>
                <p className="font-bold text-white flex-1" style={{ textShadow: '0 0 8px rgba(255,255,255,0.3)' }}>{faq.question}</p>
                <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${openIndex === index ? 'rotate-90' : ''}`} />
              </div>
              {openIndex === index && (
                <div className="px-4 pb-4 pt-0 ml-11 animate-fadeIn">
                  <p className="text-gray-400 text-sm">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="text-center mt-8">
          <Button className="bg-[#ff6b35] hover:bg-[#e55a2b] rounded-full" onClick={() => window.open(WA_LINK, '_blank')}>
            <MessageCircle className="w-4 h-4 mr-2" /> Altre domande? Scrivimi
          </Button>
        </div>
      </div>
    </section>
  );
};

export default function StartPage() {
  return (
    <>
      <StartPricingCard />
      <SectionDivider />
      <ServicesSection />
      <ProfileRatingSection />
      <SectionDivider />
      <FAQSection />
      <WhatsAppBookingForm pacchetto="Start" color="#3B82F6" />
      <CrossSellingGift pacchetto="Start" />
      <CrossSellingProducts pacchetto="Start" />
      <ContactSection />
    </>
  );
}
